import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from '../../lib/axios';
import { getTenants } from '../../services/apiSuperAdmin';
import {
    getSuperAdminSyncSettings,
    getSuperAdminSpreadsheets,
    getSuperAdminSpreadsheetTabs,
    triggerSuperAdminSync
} from '../../services/apiSuperAdmin';
import { 
  FileSpreadsheet, 
  Link2, 
  Webhook, 
  Zap, 
  CheckCircle2, 
  Loader2, 
  AlertCircle,
  Database,
  RefreshCw,
  Search,
  ChevronDown,
  Layers,
  Activity,
  ArrowRight,
  Building2,
  UserCheck,
  ShieldCheck,
  User,
  Users
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SuperAdminInventorySync = () => {
    const [selectedTenantId, setSelectedTenantId] = useState('');
    const [selectedSourceTenantId, setSelectedSourceTenantId] = useState('');
    const [syncSource, setSyncSource] = useState('tenant'); // 'tenant', 'superadmin', or 'other_tenant'
    const [loadingSettings, setLoadingSettings] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [spreadsheets, setSpreadsheets] = useState([]);
    const [tabs, setTabs] = useState([]);
    const [loadingTabs, setLoadingTabs] = useState(false);
    const [searching, setSearching] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    const [settings, setSettings] = useState({
        superadmin: {
            is_google_connected: false,
            google_email: '',
            webhook_url: '',
            last_sync_at: null
        },
        tenant: null,
        other_tenant: null
    });
    
    const [message, setMessage] = useState({ type: '', text: '' });

    // Fetch all tenants for selection
    const { data: tenantsRaw, isLoading: loadingTenants } = useQuery({
        queryKey: ['super-admin-tenants'],
        queryFn: getTenants,
    });
    const tenants = Array.isArray(tenantsRaw) ? tenantsRaw : [];

    useEffect(() => {
        // Fetch initial settings
        fetchSyncSettings(null, null);

        // Handle click outside dropdown
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Load settings when target tenant or source tenant changes
    useEffect(() => {
        fetchSyncSettings(selectedTenantId || null, selectedSourceTenantId || null);
    }, [selectedTenantId, selectedSourceTenantId]);

    // Load spreadsheets when parameters change
    useEffect(() => {
        let isConnected = false;
        if (syncSource === 'superadmin') {
            isConnected = settings.superadmin.is_google_connected;
        } else if (syncSource === 'tenant') {
            isConnected = settings.tenant?.is_google_connected;
        } else if (syncSource === 'other_tenant') {
            isConnected = settings.other_tenant?.is_google_connected;
        }

        if (!isConnected) {
            setSpreadsheets([]);
            setTabs([]);
            setSearchTerm('');
            return;
        }

        // Validate source tenant selection
        if (syncSource === 'other_tenant' && !selectedSourceTenantId) {
            setSpreadsheets([]);
            setTabs([]);
            setSearchTerm('');
            return;
        }

        const timer = setTimeout(() => {
            if (searchTerm.length >= 2 || searchTerm.length === 0) {
                fetchSpreadsheets(selectedTenantId, syncSource, selectedSourceTenantId, searchTerm);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm, selectedTenantId, syncSource, selectedSourceTenantId, settings.superadmin.is_google_connected, settings.tenant?.is_google_connected, settings.other_tenant?.is_google_connected]);

    const fetchSyncSettings = async (tenantId, sourceTenantId) => {
        setLoadingSettings(true);
        try {
            const data = await getSuperAdminSyncSettings(tenantId, sourceTenantId);
            setSettings({
                superadmin: data.superadmin,
                tenant: data.tenant,
                other_tenant: data.other_tenant
            });
            
            // Clean sheet parameters on switch
            setTabs([]);
            setSpreadsheets([]);
            setSearchTerm('');
        } catch (error) {
            console.error('Error fetching settings:', error);
            setMessage({ type: 'error', text: 'Error al obtener los ajustes de sincronización.' });
        } finally {
            setLoadingSettings(false);
        }
    };

    const handleConnectGoogle = async () => {
        setConnecting(true);
        try {
            const response = await axios.get('/api/super-admin/oauth/google/url');
            window.location.href = response.data.url;
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al obtener la URL de autenticación.' });
            setConnecting(false);
        }
    };

    const fetchSpreadsheets = async (tenantId, source, sourceTenantId, search = '') => {
        if (source === 'tenant' && !tenantId) return;
        if (source === 'other_tenant' && !sourceTenantId) return;

        setSearching(true);
        setSpreadsheets([]);
        try {
            const files = await getSuperAdminSpreadsheets(tenantId, source, sourceTenantId, search);
            setSpreadsheets(files);
        } catch (error) {
            console.error('Error fetching spreadsheets:', error);
        } finally {
            setSearching(false);
        }
    };

    const fetchTabs = async (spreadsheetId) => {
        setLoadingTabs(true);
        setTabs([]);
        try {
            const list = await getSuperAdminSpreadsheetTabs(selectedTenantId, syncSource, selectedSourceTenantId, spreadsheetId);
            setTabs(list);
            if (list.length > 0) {
               const currentSource = syncSource;
               setSettings(prev => {
                   if (currentSource === 'superadmin') {
                       return {
                           ...prev,
                           superadmin: { ...prev.superadmin, sheet_name: list[0] }
                       };
                   } else if (currentSource === 'other_tenant') {
                       return {
                           ...prev,
                           other_tenant: { ...prev.other_tenant, sheet_name: list[0] }
                       };
                   } else {
                       return {
                           ...prev,
                           tenant: { ...prev.tenant, sheet_name: list[0] }
                       };
                   }
               });
            }
        } catch (error) {
            console.error('Error fetching tabs:', error);
            const errorMsg = error.response?.data?.error || 'Error al cargar las pestañas.';
            setMessage({ type: 'error', text: errorMsg });
        } finally {
            setLoadingTabs(false);
        }
    };

    const handleSelectSpreadsheet = (ss) => {
        const currentSource = syncSource;
        setSettings(prev => {
            if (currentSource === 'superadmin') {
                return {
                    ...prev,
                    superadmin: { ...prev.superadmin, spreadsheet_id: ss.id, spreadsheet_name: ss.name, sheet_name: '' }
                };
            } else if (currentSource === 'other_tenant') {
                return {
                    ...prev,
                    other_tenant: { ...prev.other_tenant, spreadsheet_id: ss.id, spreadsheet_name: ss.name, sheet_name: '' }
                };
            } else {
                return {
                    ...prev,
                    tenant: { ...prev.tenant, spreadsheet_id: ss.id, spreadsheet_name: ss.name, sheet_name: '' }
                };
            }
        });
        setSearchTerm(ss.name);
        setShowDropdown(false);
        fetchTabs(ss.id);
    };

    const getActiveConfig = () => {
        if (syncSource === 'superadmin') {
            return settings.superadmin;
        } else if (syncSource === 'other_tenant') {
            return settings.other_tenant || {};
        }
        return settings.tenant || {};
    };

    const handleSync = async () => {
        const config = getActiveConfig();
        const sheetName = config.sheet_name;
        const spreadsheetId = config.spreadsheet_id;
        const webhookUrl = config.webhook_url;

        if (!selectedTenantId || !spreadsheetId || !webhookUrl || !sheetName) {
            setMessage({ type: 'error', text: 'Por favor complete todos los campos requeridos.' });
            return;
        }

        if (syncSource === 'other_tenant' && !selectedSourceTenantId) {
            setMessage({ type: 'error', text: 'Por favor seleccione el cliente de origen.' });
            return;
        }

        setSyncing(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await triggerSuperAdminSync({
                source: syncSource,
                tenant_id: selectedTenantId,
                source_tenant_id: selectedSourceTenantId || null,
                spreadsheet_id: spreadsheetId,
                sheet_name: sheetName,
                webhook_url: webhookUrl
            });
            setMessage({ type: 'success', text: response.message || 'Sincronización iniciada con éxito.' });
            fetchSyncSettings(selectedTenantId, selectedSourceTenantId);
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Error al iniciar la sincronización.';
            setMessage({ type: 'error', text: errorMsg });
        } finally {
            setSyncing(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Nunca';
        try {
            return new Intl.DateTimeFormat('es-ES', {
                dateStyle: 'medium',
                timeStyle: 'short'
            }).format(new Date(dateString));
        } catch (e) {
            return 'Formato inválido';
        }
    };

    const filteredSpreadsheets = spreadsheets.filter(ss => 
        ss.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const activeConfig = getActiveConfig();
    const filteredTenants = tenants.filter(t => t.id !== parseInt(selectedTenantId));

    return (
        <div className="flex-1 overflow-auto bg-slate-50 dark:bg-background-dark p-4 md:p-8 max-w-5xl mx-auto space-y-8">
            {/* Header & Super Admin Connection State */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-violet-600/5 blur-[100px] -mr-32 -mt-32 rounded-full"></div>
                <div className="relative z-10 flex items-center gap-5">
                    <div className="p-4 bg-violet-600 rounded-2xl shadow-lg shadow-violet-500/20">
                        <FileSpreadsheet className="text-white" size={32} />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
                            Sincronización Global
                            <span className="text-[10px] bg-violet-500/10 text-violet-600 dark:text-violet-400 px-2 py-1 rounded-full border border-violet-500/20 uppercase tracking-widest leading-none font-bold">Super Admin</span>
                        </h1>
                        <p className="text-slate-500 dark:text-gray-400 font-medium mt-1">
                            Actualiza los productos del cliente utilizando tus credenciales o las de otro inquilino.
                        </p>
                    </div>
                </div>

                {/* Super Admin Google Account */}
                <div className="relative z-10">
                    {settings.superadmin.is_google_connected ? (
                        <div className="flex flex-col items-end gap-1">
                            <span className="flex items-center gap-2 bg-emerald-500/10 text-emerald-600 px-4 py-2 rounded-xl border border-emerald-500/20 font-bold text-xs">
                                <ShieldCheck size={16} /> Tu Cuenta Conectada
                            </span>
                            <span className="text-[10px] font-mono text-slate-400">{settings.superadmin.google_email}</span>
                        </div>
                    ) : (
                        <button
                            onClick={handleConnectGoogle}
                            disabled={connecting}
                            className="px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-xl flex items-center justify-center gap-2 font-bold uppercase text-xs transition-all shadow-lg hover:scale-105"
                        >
                            {connecting ? <Loader2 className="animate-spin" size={16} /> : <Link2 size={14} />}
                            Conectar Mi Google (Admin)
                        </button>
                    )}
                </div>
            </div>

            {/* Tenant and Source Selection */}
            <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-lg space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Left Column: Tenant Selection */}
                    <div className="space-y-3">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                            <Building2 size={14} /> Seleccionar Cliente Destino (Base de datos)
                        </label>
                        <div className="relative">
                            <select
                                value={selectedTenantId}
                                onChange={(e) => {
                                    setSelectedTenantId(e.target.value);
                                    if (e.target.value === selectedSourceTenantId) {
                                        setSelectedSourceTenantId('');
                                    }
                                }}
                                disabled={loadingTenants}
                                className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all font-bold appearance-none cursor-pointer"
                            >
                                <option value="">-- Selecciona una Organización --</option>
                                {tenants.map(tenant => (
                                    <option key={tenant.id} value={tenant.id}>
                                        {tenant.name} ({tenant.slug})
                                    </option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={20} />
                        </div>
                    </div>

                    {/* Right Column: Source Credentials Toggle */}
                    <div className="space-y-3">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                            <Database size={14} /> Origen de Credenciales Google
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                            <button
                                onClick={() => setSyncSource('tenant')}
                                className={`py-4 px-3 rounded-xl border font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
                                    syncSource === 'tenant'
                                    ? 'bg-violet-600 text-white border-violet-700 shadow-md'
                                    : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200 dark:bg-white/5 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/10'
                                }`}
                            >
                                <User size={14} /> Cliente Destino
                            </button>
                            <button
                                onClick={() => setSyncSource('other_tenant')}
                                className={`py-4 px-3 rounded-xl border font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
                                    syncSource === 'other_tenant'
                                    ? 'bg-violet-600 text-white border-violet-700 shadow-md'
                                    : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200 dark:bg-white/5 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/10'
                                }`}
                            >
                                <Users size={14} /> Otro Cliente
                            </button>
                            <button
                                onClick={() => setSyncSource('superadmin')}
                                className={`py-4 px-3 rounded-xl border font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
                                    syncSource === 'superadmin'
                                    ? 'bg-violet-600 text-white border-violet-700 shadow-md'
                                    : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200 dark:bg-white/5 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/10'
                                }`}
                            >
                                <ShieldCheck size={14} /> Mis Credenciales (Admin)
                            </button>
                        </div>
                    </div>
                </div>

                {/* Additional selector for other tenant credentials */}
                {syncSource === 'other_tenant' && selectedTenantId && (
                    <div className="space-y-3 pt-4 border-t border-slate-100 dark:border-white/5 animate-in fade-in slide-in-from-top-2 duration-300">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                            <Building2 size={14} /> Seleccionar Cliente Origen (Credenciales Google)
                        </label>
                        <div className="relative max-w-md">
                            <select
                                value={selectedSourceTenantId}
                                onChange={(e) => setSelectedSourceTenantId(e.target.value)}
                                className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all font-bold appearance-none cursor-pointer"
                            >
                                <option value="">-- Selecciona el Cliente Origen --</option>
                                {filteredTenants.map(tenant => (
                                    <option key={tenant.id} value={tenant.id}>
                                        {tenant.name} ({tenant.slug})
                                    </option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={20} />
                        </div>
                    </div>
                )}

                {/* Source Connection Info */}
                {selectedTenantId && (
                    <div className="pt-2">
                        {syncSource === 'tenant' ? (
                            settings.tenant?.is_google_connected ? (
                                <div className="flex items-center gap-2.5 text-xs text-slate-600 dark:text-gray-300 font-medium bg-slate-50 dark:bg-white/5 px-4 py-3 rounded-xl border border-slate-200 dark:border-white/5">
                                    <UserCheck size={16} className="text-emerald-500" />
                                    <span>
                                        Conectado con cuenta del cliente destino: <strong>{settings.tenant.user_name}</strong> ({settings.tenant.google_email})
                                    </span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2.5 text-xs text-red-500 font-bold bg-red-500/5 px-4 py-3 rounded-xl border border-red-500/10">
                                    <AlertCircle size={16} />
                                    <span>El cliente seleccionado no tiene ninguna cuenta de Google conectada.</span>
                                </div>
                            )
                        ) : syncSource === 'other_tenant' ? (
                            !selectedSourceTenantId ? (
                                <div className="flex items-center gap-2.5 text-xs text-amber-500 font-bold bg-amber-500/5 px-4 py-3 rounded-xl border border-amber-500/10">
                                    <AlertCircle size={16} />
                                    <span>Selecciona un cliente de origen para leer las hojas de cálculo.</span>
                                </div>
                            ) : settings.other_tenant?.is_google_connected ? (
                                <div className="flex items-center gap-2.5 text-xs text-slate-600 dark:text-gray-300 font-medium bg-slate-50 dark:bg-white/5 px-4 py-3 rounded-xl border border-slate-200 dark:border-white/5">
                                    <UserCheck size={16} className="text-emerald-500" />
                                    <span>
                                        Conectado con cuenta del cliente de origen: <strong>{settings.other_tenant.user_name}</strong> ({settings.other_tenant.google_email})
                                    </span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2.5 text-xs text-red-500 font-bold bg-red-500/5 px-4 py-3 rounded-xl border border-red-500/10">
                                    <AlertCircle size={16} />
                                    <span>El cliente de origen seleccionado no tiene ninguna cuenta de Google conectada.</span>
                                </div>
                            )
                        ) : (
                            settings.superadmin.is_google_connected ? (
                                <div className="flex items-center gap-2.5 text-xs text-slate-600 dark:text-gray-300 font-medium bg-slate-50 dark:bg-white/5 px-4 py-3 rounded-xl border border-slate-200 dark:border-white/5">
                                    <ShieldCheck size={16} className="text-violet-500" />
                                    <span>
                                        Usando tu cuenta de Super Admin conectada: <strong>{settings.superadmin.google_email}</strong>
                                    </span>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between gap-2.5 text-xs text-red-500 font-bold bg-red-500/5 px-4 py-3 rounded-xl border border-red-500/10 w-full">
                                    <div className="flex items-center gap-2.5">
                                        <AlertCircle size={16} />
                                        <span>No has conectado tu cuenta de Google de Super Admin.</span>
                                    </div>
                                    <button
                                        onClick={handleConnectGoogle}
                                        disabled={connecting}
                                        className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors uppercase text-[10px] tracking-wider font-black flex items-center gap-1 shrink-0"
                                    >
                                        {connecting ? <Loader2 className="animate-spin" size={12} /> : <Link2 size={12} />}
                                        Conectar ahora
                                    </button>
                                </div>
                            )
                        )}
                    </div>
                )}
            </div>

            {selectedTenantId && (
                <>
                    {loadingSettings ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="animate-spin text-violet-600" size={32} />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-in fade-in duration-300">
                            {/* Data Source Panel */}
                            <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-lg space-y-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-500/10 rounded-lg text-blue-600">
                                        <Database size={20} />
                                    </div>
                                    <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Datos de Origen</h2>
                                </div>

                                {activeConfig.is_google_connected ? (
                                    <div className="space-y-6">
                                        {/* Spreadsheet Searchable Select */}
                                        <div className="space-y-2 relative" ref={dropdownRef}>
                                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                                <Search size={12} /> Buscar Hoja de Cálculo
                                            </label>
                                            <div className="relative">
                                                <input 
                                                    type="text"
                                                    placeholder="Escribe para buscar..."
                                                    value={searchTerm}
                                                    onFocus={() => setShowDropdown(true)}
                                                    onChange={(e) => {
                                                        setSearchTerm(e.target.value);
                                                        setShowDropdown(true);
                                                    }}
                                                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all font-medium pr-12"
                                                />
                                                {searching ? (
                                                    <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 text-violet-600 animate-spin" size={20} />
                                                ) : (
                                                    <ChevronDown className={`absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none transition-transform ${showDropdown ? 'rotate-180' : ''}`} size={20} />
                                                )}
                                            </div>

                                            <AnimatePresence>
                                                {showDropdown && (
                                                    <motion.div 
                                                        initial={{ opacity: 0, y: -10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: -10 }}
                                                        className="absolute z-50 w-full mt-2 bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 rounded-2xl shadow-2xl overflow-hidden max-h-64 overflow-y-auto"
                                                    >
                                                        {searching ? (
                                                            <div className="p-8 text-center flex flex-col items-center gap-3">
                                                                <Loader2 className="animate-spin text-violet-500/50" size={24} />
                                                                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Buscando en Google Drive...</span>
                                                            </div>
                                                        ) : filteredSpreadsheets.length > 0 ? (
                                                            filteredSpreadsheets.map(ss => (
                                                                <button
                                                                    key={ss.id}
                                                                    onClick={() => handleSelectSpreadsheet(ss)}
                                                                    className="w-full text-left px-5 py-4 hover:bg-violet-500/5 dark:hover:bg-white/5 transition-colors border-b border-slate-100 dark:border-white/5 last:border-0 flex items-center justify-between group"
                                                                >
                                                                    <span className="font-bold text-sm text-slate-700 dark:text-slate-200 group-hover:text-violet-600 transition-colors">{ss.name}</span>
                                                                    <ArrowRight size={14} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0" />
                                                                </button>
                                                            ))
                                                        ) : (
                                                            <div className="p-8 text-center text-slate-400 text-sm font-medium">
                                                                {searchTerm.length < 2 ? 'Escribe al menos 2 caracteres...' : 'No se encontraron resultados'}
                                                            </div>
                                                        )}
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>

                                        {/* Tab Selector */}
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                                <Layers size={12} /> Seleccionar Pestaña
                                            </label>
                                            <div className="relative">
                                                <select 
                                                    value={activeConfig.sheet_name || ''}
                                                    disabled={!activeConfig.spreadsheet_id || loadingTabs}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        const currentSource = syncSource;
                                                        setSettings(prev => {
                                                            if (currentSource === 'superadmin') {
                                                                 return {
                                                                     ...prev,
                                                                     superadmin: { ...prev.superadmin, sheet_name: val }
                                                                 };
                                                            } else if (currentSource === 'other_tenant') {
                                                                 return {
                                                                     ...prev,
                                                                     other_tenant: { ...prev.other_tenant, sheet_name: val }
                                                                 };
                                                            } else {
                                                                 return {
                                                                     ...prev,
                                                                     tenant: { ...prev.tenant, sheet_name: val }
                                                                 };
                                                            }
                                                        });
                                                    }}
                                                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all font-bold appearance-none cursor-pointer"
                                                >
                                                    <option value="" disabled>{loadingTabs ? 'Cargando pestañas...' : 'Seleccione una pestaña'}</option>
                                                    {tabs.map(tab => (
                                                        <option key={tab} value={tab}>{tab}</option>
                                                    ))}
                                                </select>
                                                {loadingTabs ? (
                                                    <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 text-violet-600 animate-spin" size={20} />
                                                ) : (
                                                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={20} />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-8 text-center text-red-500 space-y-3">
                                        <AlertCircle size={32} />
                                        <h3 className="font-bold text-sm">Sin Conexión a Google</h3>
                                        <p className="text-xs text-slate-500 max-w-xs">
                                            {syncSource === 'superadmin'
                                                ? 'Conecta tu cuenta de Google para poder utilizar tus propias hojas de cálculo.'
                                                : syncSource === 'other_tenant'
                                                ? 'El cliente de origen seleccionado no tiene una cuenta de Google conectada.'
                                                : 'Este cliente destino no tiene una cuenta de Google conectada para realizar la sincronización.'
                                            }
                                        </p>
                                        {syncSource === 'superadmin' && (
                                            <button
                                                onClick={handleConnectGoogle}
                                                disabled={connecting}
                                                className="px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl flex items-center justify-center gap-2 font-bold uppercase text-xs transition-all shadow-md mt-2 animate-pulse"
                                            >
                                                {connecting ? <Loader2 className="animate-spin" size={14} /> : <Link2 size={14} />}
                                                Conectar Google
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Automation Panel */}
                            <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-lg space-y-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-purple-500/10 rounded-lg text-purple-600">
                                        <Webhook size={20} />
                                    </div>
                                    <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Configuración de Automatización</h2>
                                </div>

                                <div className="space-y-4">
                                     <div className="space-y-2">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Webhook de Sincronización</label>
                                        <div className="relative">
                                            <input 
                                                type="text"
                                                placeholder="https://servidor.com/webhook/..."
                                                value={activeConfig.webhook_url || ''}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    const currentSource = syncSource;
                                                    setSettings(prev => {
                                                        if (currentSource === 'superadmin') {
                                                            return {
                                                                ...prev,
                                                                superadmin: { ...prev.superadmin, webhook_url: val }
                                                            };
                                                        } else if (currentSource === 'other_tenant') {
                                                            return {
                                                                ...prev,
                                                                other_tenant: { ...prev.other_tenant, webhook_url: val }
                                                            };
                                                        } else {
                                                            return {
                                                                ...prev,
                                                                tenant: { ...prev.tenant, webhook_url: val }
                                                            };
                                                        }
                                                    });
                                                }}
                                                className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 pl-12 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all font-medium"
                                            />
                                            <Link2 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                        </div>
                                    </div>

                                    <div className="pt-4">
                                        <button
                                            onClick={handleSync}
                                            disabled={syncing || !activeConfig.is_google_connected || !activeConfig.spreadsheet_id || (syncSource === 'other_tenant' && !selectedSourceTenantId)}
                                            className={`w-full py-5 rounded-2xl flex items-center justify-center gap-3 font-black tracking-widest uppercase text-base transition-all shadow-xl ${
                                                syncing || !activeConfig.is_google_connected || !activeConfig.spreadsheet_id || (syncSource === 'other_tenant' && !selectedSourceTenantId)
                                                ? 'bg-slate-100 text-slate-400 dark:bg-white/5 dark:text-gray-600 cursor-not-allowed'
                                                : 'bg-violet-600 text-white hover:scale-[1.02] active:scale-[0.98] shadow-violet-500/30'
                                            }`}
                                        >
                                            {syncing ? <Loader2 className="animate-spin" size={24} /> : <RefreshCw size={22} />}
                                            {syncing ? 'Sincronizando...' : 'Iniciar Sincronización'}
                                        </button>
                                    </div>
                                </div>

                                <div className="bg-slate-50 dark:bg-white/5 p-6 rounded-2xl border border-slate-100 dark:border-white/5">
                                     <div className="flex items-start gap-4">
                                        <div className="p-2 bg-yellow-500/10 rounded-lg text-yellow-600 mt-1">
                                            <Zap size={18} fill="currentColor" />
                                        </div>
                                        <div>
                                            <p className="text-xs font-black text-slate-900 dark:text-white uppercase tracking-tight">Información de Sincronización</p>
                                            <p className="text-[11px] text-slate-500 dark:text-gray-400 mt-1 leading-relaxed font-medium">
                                                Última sincronización en la base de datos de este cliente: <strong>{formatDate(settings.tenant?.last_sync_at)}</strong>.
                                            </p>
                                        </div>
                                     </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Messages */}
            {message.text && (
                <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={`p-6 rounded-[2rem] text-center text-sm font-bold shadow-lg border flex items-center justify-center gap-3 ${
                        message.type === 'success' 
                        ? 'bg-green-500 text-white border-green-600' 
                        : 'bg-red-500 text-white border-red-600'
                    }`}
                >
                    {message.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                    {message.text}
                </motion.div>
            )}
        </div>
    );
};

export default SuperAdminInventorySync;
